import { ref, readonly, type Ref } from 'vue';
import type { AuthError } from '@/types/auth';
import { isAuthError, createAuthError } from '@/types/auth';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import { AUTH_CONFIG, ENV_CONFIG } from '@/config/constants';

const useMock = AUTH_CONFIG.isMockAuthEnabled();
if (import.meta.env.MODE === 'production' && useMock) {
  throw new Error('Mock auth must not be enabled in production builds');
}

// Module-level singleton state
const isLoading = ref(false);
const error = ref<AuthError | null>(null);
const rateLimitCountdown = ref(0);

let rateLimitTimer: ReturnType<typeof setInterval> | null = null;

// --- Password Reset API interface ---

interface PasswordResetApi {
  requestReset(email: string): Promise<void>;
  confirmReset(token: string, newPassword: string): Promise<void>;
}

// --- Mock implementation ---

async function mockDelay(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.MOCK_API_DELAY));
}

const MOCK_VALID_TOKEN = 'test-reset-token';

const mockPasswordResetApi: PasswordResetApi = {
  async requestReset(_email: string): Promise<void> {
    await mockDelay();
    // Server never reveals email existence — always succeeds
  },

  async confirmReset(token: string, _newPassword: string): Promise<void> {
    await mockDelay();

    if (token !== MOCK_VALID_TOKEN) {
      throw createAuthError('invalid_token', 'This link is invalid or has expired');
    }
  },
};

// Safe default: 60s is conservative enough to prevent abuse (won't overwhelm
// the server) but short enough that users won't wait excessively if the header
// is genuinely missing. The server always sends Retry-After; this only fires
// for malformed/missing headers from unexpected intermediaries.
const DEFAULT_RETRY_AFTER_SECS = 60;

function parseRetryAfter(response: Response): number {
  const header = response.headers.get('Retry-After');
  if (header === null) {
    console.warn('[usePasswordReset] 429 response missing Retry-After header, defaulting to 60s');
    return DEFAULT_RETRY_AFTER_SECS;
  }
  const parsed = parseInt(header, 10);
  if (Number.isNaN(parsed)) {
    console.warn(
      `[usePasswordReset] Invalid Retry-After header value: '${header}', defaulting to 60s`
    );
    return DEFAULT_RETRY_AFTER_SECS;
  }
  return parsed;
}

// --- Real implementation ---

const realPasswordResetApi: PasswordResetApi = {
  async requestReset(email: string): Promise<void> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (response.status === 429) {
      throw createAuthError(
        'rate_limited',
        'Too many attempts. Please wait.',
        parseRetryAfter(response)
      );
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }
  },

  async confirmReset(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    if (response.status === 400) {
      throw createAuthError('invalid_token', 'This link is invalid or has expired');
    }

    if (response.status === 422) {
      throw createAuthError('validation', 'Password must be at least 8 characters');
    }

    if (response.status === 429) {
      throw createAuthError(
        'rate_limited',
        'Too many attempts. Please wait.',
        parseRetryAfter(response)
      );
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }
  },
};

// --- API selection ---

const api: PasswordResetApi = useMock ? mockPasswordResetApi : realPasswordResetApi;

// --- Shared helpers ---

function startRateLimitCountdown(seconds: number): void {
  rateLimitCountdown.value = seconds;
  if (rateLimitTimer !== null) {
    clearInterval(rateLimitTimer);
  }
  rateLimitTimer = setInterval(() => {
    rateLimitCountdown.value--;
    if (rateLimitCountdown.value <= 0) {
      if (rateLimitTimer !== null) {
        clearInterval(rateLimitTimer);
        rateLimitTimer = null;
      }
      rateLimitCountdown.value = 0;
    }
  }, 1000);
}

// --- Test helper ---

function resetState(): void {
  if (import.meta.env.MODE === 'production') return;
  isLoading.value = false;
  error.value = null;
  rateLimitCountdown.value = 0;
  if (rateLimitTimer !== null) {
    clearInterval(rateLimitTimer);
    rateLimitTimer = null;
  }
}

// --- Public composable ---

export function usePasswordReset() {
  async function requestReset(email: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await api.requestReset(email);
    } catch (err: unknown) {
      if (isAuthError(err)) {
        if (err.type === 'rate_limited' && err.retryAfterSeconds) {
          startRateLimitCountdown(err.retryAfterSeconds);
        }
        error.value = err;
      } else {
        reportClientError(err, ErrorLocation.PasswordResetRequestUnexpected);
        error.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw error.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function confirmReset(token: string, newPassword: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await api.confirmReset(token, newPassword);
    } catch (err: unknown) {
      if (isAuthError(err)) {
        if (err.type === 'rate_limited' && err.retryAfterSeconds) {
          startRateLimitCountdown(err.retryAfterSeconds);
        }
        error.value = err;
      } else {
        reportClientError(err, ErrorLocation.PasswordResetConfirmUnexpected);
        error.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw error.value;
    } finally {
      isLoading.value = false;
    }
  }

  function clearError(): void {
    error.value = null;
  }

  return {
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,
    error: readonly(error) as Readonly<Ref<AuthError | null>>,
    rateLimitCountdown: readonly(rateLimitCountdown) as Readonly<Ref<number>>,
    requestReset,
    confirmReset,
    clearError,
    _resetState: resetState,
  };
}
